import type { ReactNode } from 'react';
import { useOp } from '../../api.ts';
import type { MarketList } from './types.ts';

/** Market (country) selector backed by admin.markets.list; empty value = the user's first market. */
export function MarketPicker({ value, onChange, onlyMine = true, label = 'Market', allowEmpty = false }: { value: string; onChange: (code: string) => void; onlyMine?: boolean; label?: string; allowEmpty?: boolean }) {
  const list = useOp<MarketList>('admin.markets.list', {});
  const rows = (list.data?.rows ?? []).filter((m) => !onlyMine || m.mySites > 0);
  return (
    <label className="inline-field">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        {allowEmpty || !value ? <option value="">{allowEmpty ? 'Any' : 'Default'}</option> : null}
        {rows.map((m) => (
          <option key={m.countryCode} value={m.countryCode}>
            {m.countryCode} · {m.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Starts a browser download of text the API returned (Studio is a normal web app, so this works). */
export function downloadText(filename: string, text: string, type = 'text/csv;charset=utf-8'): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function Counter({ value, limit }: { value: string; limit: number }) {
  const length = value.length;
  return (
    <small className={length > limit ? 'text-warn' : 'muted'} aria-live="polite">
      {length}/{limit}
    </small>
  );
}

export function Tabs({ children }: { children: ReactNode }) {
  return (
    <nav className="cat-tabs" aria-label="Section">
      {children}
    </nav>
  );
}

/** "My Shop" → "my-shop", the same rule the API uses. */
export function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/** Replaces {variables} like the site renderer does; unknown ones stay visible. */
export function resolveVariables(text: string, variables: Record<string, string>): string {
  return text.replace(/\{([a-z_]+)\}/g, (match, key: string) => variables[key] ?? match);
}
